import React, { useState, useEffect, useCallback } from 'react';
import {
  CalendarDays, Link2, Copy, Check, RefreshCw, Loader2, AlertCircle, ExternalLink,
  Unplug, Plug, User, Users, Building2, RotateCcw, X, Info,
} from 'lucide-react';
import apiService from '../services/api';

// Calendar sync: put leave into Outlook, Google Calendar, Apple Calendar etc.
//   1. Connect Outlook / Google — approved leave is added the moment it's approved
//   2. Subscribe with a link — works with any calendar app, refreshes every few hours
//
// Theming: the page detects light / dark itself (see useThemeMode) and sets
// data-ig-theme on its root. Light mode uses the app's CSS variables where they exist;
// dark mode has its own palette, so it works even if the app's variables don't change.

const PROVIDER_INFO = {
  microsoft: {
    name: 'Outlook',
    sub: 'Microsoft 365, Outlook.com and Exchange',
    brand: '#0F5CB5',
  },
  google: {
    name: 'Google Calendar',
    sub: 'Gmail and Google Workspace',
    brand: '#1E7B3A',
  },
};

const FEED_INFO = {
  me:      { icon: User,      title: 'My leave',            desc: 'Your own leave, including requests waiting for approval (shown as tentative).' },
  team:    { icon: Users,     title: 'My department',       desc: 'Approved leave for everyone in your department.' },
  company: { icon: Building2, title: 'Everyone',            desc: 'Approved leave for the whole company.' },
};

function timeAgo(date) {
  if (!date) return null;
  const s = Math.round((Date.now() - new Date(date).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function errorText(err, fallback) {
  return err?.response?.data?.message || fallback;
}

// Links that open each app's "subscribe to calendar" screen with the feed filled in
const subscribeLinks = (feed, name) => ({
  outlook:       `https://outlook.office.com/calendar/addfromweb?url=${encodeURIComponent(feed.url)}&name=${encodeURIComponent(name)}`,
  outlookPersonal: `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(feed.url)}&name=${encodeURIComponent(name)}`,
  google:        `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(feed.webcal)}`,
  apple:         feed.webcal,
});

// ─── Light / dark detection ───
// Looks for the usual dark-mode markers on <html>/<body> (class "dark", data-theme,
// data-mode, data-bs-theme, colour-scheme ...). If none are found, falls back to the
// real background colour behind the page. Re-checks whenever those change.
function parseRgb(str) {
  const m = str && str.match(/rgba?\(([^)]+)\)/);
  if (!m) return null;
  const [r, g, b, a = 1] = m[1].split(/[\s,/]+/).filter(Boolean).map(Number);
  return { r, g, b, a };
}

const THEME_ATTRS = ['data-theme', 'data-mode', 'data-color-scheme', 'data-color-mode', 'data-bs-theme'];

function detectDark(el) {
  if (typeof document === 'undefined') return false;
  for (const node of [document.documentElement, document.body]) {
    if (!node) continue;
    const cls = typeof node.className === 'string' ? node.className : '';
    if (/(^|\s)(dark|dark-mode|theme-dark|is-dark)(\s|$)/i.test(cls)) return true;
    if (/(^|\s)(light|light-mode|theme-light|is-light)(\s|$)/i.test(cls)) return false;
    for (const attr of THEME_ATTRS) {
      const v = node.getAttribute(attr);
      if (v) return /dark/i.test(v);
    }
    const cs = node.style?.colorScheme;
    if (cs === 'dark') return true;
    if (cs === 'light') return false;
  }
  // No marker found: look at the first solid background behind the page
  let node = el?.parentElement || document.body;
  while (node) {
    if (node.hasAttribute('data-ig-painted')) { node = node.parentElement; continue; }
    const c = parseRgb(getComputedStyle(node).backgroundColor);
    if (c && c.a > 0.5) return (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255 < 0.4;
    node = node.parentElement;
  }
  return false;
}

function useThemeMode(el) {
  const [dark, setDark] = useState(() => detectDark(null));
  useEffect(() => {
    const check = () => setDark(detectDark(el));
    check();
    const obs = new MutationObserver(check);
    const opts = { attributes: true, attributeFilter: ['class', 'style', ...THEME_ATTRS] };
    obs.observe(document.documentElement, opts);
    if (document.body) obs.observe(document.body, opts);
    // Also watch the layout wrappers above this page (theme class on a parent div)
    for (let p = el?.parentElement; p && p !== document.body; p = p.parentElement) obs.observe(p, opts);
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    mq?.addEventListener?.('change', check);
    window.addEventListener('storage', check);
    const t = setTimeout(check, 50); // some apps apply the theme a tick after the toggle
    return () => {
      obs.disconnect();
      mq?.removeEventListener?.('change', check);
      window.removeEventListener('storage', check);
      clearTimeout(t);
    };
  }, [el]);
  return dark ? 'dark' : 'light';
}

// The app's layout keeps its light content background in dark mode, which leaves the
// page header unreadable. While dark, paint the nearest light background behind this
// page dark, and put it back exactly as it was when switching to light or leaving.
const DARK_PAGE_BG = '#0d0f13';

function usePaintBackdrop(el, theme) {
  useEffect(() => {
    if (!el || theme !== 'dark') return undefined;
    let target = null;
    for (let p = el.parentElement; p; p = p.parentElement) {
      const c = parseRgb(getComputedStyle(p).backgroundColor);
      if (c && c.a > 0.5) {
        const lum = (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;
        if (lum > 0.5) target = p;
        break;
      }
    }
    if (!target) return undefined;
    const prev = { bg: target.style.backgroundColor, cs: target.style.colorScheme };
    target.setAttribute('data-ig-painted', '');
    target.style.backgroundColor = DARK_PAGE_BG;
    target.style.colorScheme = 'dark';
    return () => {
      target.style.backgroundColor = prev.bg;
      target.style.colorScheme = prev.cs;
      target.removeAttribute('data-ig-painted');
    };
  }, [el, theme]);
}

export default function Integrations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [notice, setNotice] = useState(null);     // { tone: 'ok' | 'bad' | 'info', text }
  const [busy, setBusy] = useState({});            // { [key]: true } while an action runs
  const [copied, setCopied] = useState(null);
  const [rootEl, setRootEl] = useState(null);
  const theme = useThemeMode(rootEl);
  usePaintBackdrop(rootEl, theme);

  const shell = children => (
    <div ref={setRootEl} className="ig-page" data-ig-theme={theme}>
      <style>{STYLES}</style>
      {children}
    </div>
  );

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await apiService.getCalendarSettings();
      setData(res.data);
    } catch (err) {
      setLoadError(errorText(err, "Couldn't load your calendar settings."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Coming back from the Microsoft / Google sign-in page
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const result = q.get('calendar');
    if (!result) return;
    const name = PROVIDER_INFO[q.get('provider')]?.name || 'Your calendar';
    const reasons = {
      expired: 'The sign-in took too long. Please try again.',
      no_refresh: `${name} didn't give lasting access. Please try again and accept all the permissions.`,
    };
    if (result === 'connected') setNotice({ tone: 'ok', text: `${name} connected. Your upcoming approved leave is being added now.` });
    else if (result === 'cancelled') setNotice({ tone: 'info', text: `${name} wasn't connected — the sign-in was cancelled.` });
    else setNotice({ tone: 'bad', text: reasons[q.get('reason')] || `Couldn't connect ${name}. Please try again.` });
    // Tidy the address bar
    q.delete('calendar'); q.delete('provider'); q.delete('reason');
    const rest = q.toString();
    window.history.replaceState(window.history.state, '', window.location.pathname + (rest ? `?${rest}` : ''));
    // Give the background sync a moment, then refresh the "last synced" time
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [load]);

  const run = async (key, fn) => {
    setBusy(b => ({ ...b, [key]: true }));
    try { await fn(); } finally { setBusy(b => ({ ...b, [key]: false })); }
  };

  const connect = provider => run(`connect-${provider}`, async () => {
    try {
      const res = await apiService.connectCalendar(provider);
      window.location.assign(res.data.url);
    } catch (err) {
      setNotice({ tone: 'bad', text: errorText(err, "Couldn't start the sign-in. Please try again.") });
    }
  });

  const syncNow = conn => run(`sync-${conn.id}`, async () => {
    try {
      const res = await apiService.syncCalendarConnection(conn.id);
      const { synced, removed, failed } = res.data;
      setNotice({
        tone: failed ? 'bad' : synced ? 'ok' : 'info',
        text: failed
          ? `Synced ${synced} ${synced === 1 ? 'absence' : 'absences'}, but ${failed} couldn't be updated.`
          : synced
            ? `Up to date: ${synced} upcoming ${synced === 1 ? 'absence' : 'absences'} in your calendar${removed ? `, ${removed} removed` : ''}.`
            : `You have no approved leave from today onwards, so there's nothing to add yet${removed ? ` (${removed} old ${removed === 1 ? 'event' : 'events'} removed)` : ''}. Leave in the past isn't added, and requests appear once they're approved.`,
      });
      await load();
    } catch (err) {
      setNotice({ tone: 'bad', text: errorText(err, "Couldn't sync right now.") });
      await load();
    }
  });

  const toggleOof = conn => run(`oof-${conn.id}`, async () => {
    try {
      await apiService.updateCalendarConnection(conn.id, { outOfOffice: !conn.outOfOffice });
      await load();
    } catch (err) {
      setNotice({ tone: 'bad', text: errorText(err, "Couldn't change that setting.") });
    }
  });

  const disconnect = conn => {
    const name = PROVIDER_INFO[conn.provider]?.name || 'this calendar';
    if (!window.confirm(`Disconnect ${name}? Leave that was added to it will be removed.`)) return;
    run(`disconnect-${conn.id}`, async () => {
      try {
        const res = await apiService.disconnectCalendar(conn.id);
        const n = res.data?.removed ?? 0;
        setNotice({ tone: 'ok', text: `${name} disconnected${n ? ` and ${n} ${n === 1 ? 'event' : 'events'} removed` : ''}.` });
        await load();
      } catch (err) {
        setNotice({ tone: 'bad', text: errorText(err, "Couldn't disconnect. Please try again.") });
      }
    });
  };

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Older browsers
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(key);
    setTimeout(() => setCopied(c => (c === key ? null : c)), 2000);
  };

  const resetLinks = () => {
    if (!window.confirm('Reset your calendar links? Any calendar already subscribed with the old links will stop updating, and you\'ll need to add the new ones.')) return;
    run('reset', async () => {
      try {
        await apiService.resetCalendarFeeds();
        setNotice({ tone: 'ok', text: 'New links created. The old ones no longer work.' });
        await load();
      } catch (err) {
        setNotice({ tone: 'bad', text: errorText(err, "Couldn't reset the links.") });
      }
    });
  };

  if (loading) {
    return shell(
      <div className="ig-loading"><Loader2 size={18} className="animate-spin" /> Loading calendar settings…</div>
    );
  }

  if (loadError || !data) {
    return shell(
      <div className="ig-notice ig-notice-bad">
        <AlertCircle size={16} /> <span>{loadError || 'Something went wrong.'}</span>
        <button type="button" className="ig-link" onClick={() => { setLoading(true); load(); }}>Try again</button>
      </div>
    );
  }

  const connections = data.connections || [];
  const connectionFor = p => connections.find(c => c.provider === p);
  const anyDirect = connections.some(c => c.status === 'active');
  const feedEntries = Object.entries(data.feeds || {}).filter(([, f]) => f);

  return shell(
    <>
      <header className="ig-head">
        <span className="ig-head-icon"><CalendarDays size={20} /></span>
        <div>
          <h1>Calendar sync</h1>
          <p>See leave in the calendar you already use. Connect Outlook or Google for instant updates, or subscribe with a link in any calendar app.</p>
        </div>
      </header>

      {notice && (
        <div className={`ig-notice ig-notice-${notice.tone}`} role="status">
          {notice.tone === 'ok' ? <Check size={16} /> : notice.tone === 'bad' ? <AlertCircle size={16} /> : <Info size={16} />}
          <span>{notice.text}</span>
          <button type="button" className="ig-x" onClick={() => setNotice(null)} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}

      {/* ─── 1. Direct connections ─── */}
      <section className="ig-section">
        <div className="ig-section-head">
          <h2><Plug size={16} /> Connect your calendar</h2>
          <p>Your approved leave is added to your calendar as soon as it's approved, and removed if it's cancelled.</p>
        </div>

        <div className="ig-providers">
          {Object.entries(PROVIDER_INFO).map(([id, info]) => {
            const configured = data.providers?.[id]?.configured;
            const conn = connectionFor(id);
            const needsReconnect = conn?.status === 'needs_reconnect';
            return (
              <div key={id} className="ig-card" data-state={conn ? (needsReconnect ? 'warn' : 'on') : 'off'}>
                <div className="ig-card-top">
                  <span className="ig-badge" style={{ '--brand': info.brand }}><CalendarDays size={18} /></span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p className="ig-card-title">{info.name}</p>
                    <p className="ig-card-sub">
                      {conn
                        ? <>Connected as <b>{conn.accountEmail || 'your account'}</b></>
                        : info.sub}
                    </p>
                  </div>
                  {conn && !needsReconnect && <span className="ig-pill ig-pill-ok"><span className="ig-dot" /> Syncing</span>}
                  {needsReconnect && <span className="ig-pill ig-pill-warn">Needs reconnecting</span>}
                </div>

                {conn && (
                  <div className="ig-card-body">
                    {needsReconnect ? (
                      <p className="ig-warn-text"><AlertCircle size={14} /> {conn.lastError || 'Access expired. Reconnect to keep your calendar up to date.'}</p>
                    ) : (
                      <p className="ig-meta">
                        {conn.lastSyncedAt ? `Last checked ${timeAgo(conn.lastSyncedAt)}` : 'Checking for upcoming leave…'}
                        {conn.lastError && <span className="ig-warn-text"> · Last problem: {conn.lastError}</span>}
                      </p>
                    )}

                    {id === 'microsoft' && !needsReconnect && (
                      <label className="ig-switch-row">
                        <span>
                          <span className="ig-switch-label">Show as out of office</span>
                          <span className="ig-switch-hint">Otherwise leave shows as busy</span>
                        </span>
                        <span className="ig-switch">
                          <input type="checkbox" checked={!!conn.outOfOffice} onChange={() => toggleOof(conn)} disabled={busy[`oof-${conn.id}`]} />
                          <span />
                        </span>
                      </label>
                    )}
                  </div>
                )}

                <div className="ig-card-actions">
                  {!configured ? (
                    <span className="ig-meta">Not set up on this server yet — ask your administrator.</span>
                  ) : !conn ? (
                    <button type="button" className="btn-primary ig-btn" onClick={() => connect(id)} disabled={busy[`connect-${id}`]}>
                      {busy[`connect-${id}`] ? <Loader2 size={15} className="animate-spin" /> : <Plug size={15} />} Connect {info.name}
                    </button>
                  ) : needsReconnect ? (
                    <>
                      <button type="button" className="btn-primary ig-btn" onClick={() => connect(id)} disabled={busy[`connect-${id}`]}>
                        <RotateCcw size={15} /> Reconnect
                      </button>
                      <button type="button" className="ig-btn-ghost" onClick={() => disconnect(conn)} disabled={busy[`disconnect-${conn.id}`]}>
                        <Unplug size={14} /> Disconnect
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="ig-btn-ghost" onClick={() => syncNow(conn)} disabled={busy[`sync-${conn.id}`]}>
                        <RefreshCw size={14} className={busy[`sync-${conn.id}`] ? 'animate-spin' : ''} /> Sync now
                      </button>
                      <button type="button" className="ig-btn-ghost ig-btn-danger" onClick={() => disconnect(conn)} disabled={busy[`disconnect-${conn.id}`]}>
                        {busy[`disconnect-${conn.id}`] ? <Loader2 size={14} className="animate-spin" /> : <Unplug size={14} />} Disconnect
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ─── 2. Subscription links ─── */}
      <section className="ig-section">
        <div className="ig-section-head ig-section-head-row">
          <div>
            <h2><Link2 size={16} /> Subscribe with a link</h2>
            <p>Works with any calendar app. Calendar apps check for changes every few hours (Google can take up to a day), and the events are view-only.</p>
          </div>
          <button type="button" className="ig-btn-ghost" onClick={resetLinks} disabled={busy.reset} title="Make new links and switch off the old ones">
            {busy.reset ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Reset links
          </button>
        </div>

        {anyDirect && (
          <div className="ig-notice ig-notice-info" style={{ marginBottom: 12 }}>
            <Info size={16} />
            <span>Your calendar is already connected above, so you don't need the <b>My leave</b> link too, otherwise your leave would show twice.</span>
          </div>
        )}

        <div className="ig-feeds">
          {feedEntries.map(([scope, feed]) => {
            const info = FEED_INFO[scope];
            const Icon = info.icon;
            const title = scope === 'team' && feed.departments?.length ? `${feed.departments.join(', ')}` : info.title;
            const links = subscribeLinks(feed, scope === 'me' ? 'My leave' : `${title} – leave`);
            const key = `copy-${scope}`;
            return (
              <div key={scope} className="ig-feed">
                <div className="ig-feed-main">
                  <span className="ig-feed-icon"><Icon size={16} /></span>
                  <div style={{ minWidth: 0 }}>
                    <p className="ig-card-title">{title}</p>
                    <p className="ig-card-sub">{info.desc}</p>
                  </div>
                </div>
                <div className="ig-url">
                  <input readOnly value={feed.url} onFocus={e => e.target.select()} aria-label={`${title} calendar link`} />
                  <button type="button" onClick={() => copy(key, feed.url)} className={copied === key ? 'is-copied' : ''}>
                    {copied === key ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                  </button>
                </div>
                <div className="ig-open">
                  <span>Add to</span>
                  <a href={links.outlook} target="_blank" rel="noopener noreferrer">Outlook <ExternalLink size={11} /></a>
                  <a href={links.outlookPersonal} target="_blank" rel="noopener noreferrer">Outlook.com <ExternalLink size={11} /></a>
                  <a href={links.google} target="_blank" rel="noopener noreferrer">Google <ExternalLink size={11} /></a>
                  <a href={links.apple}>Apple Calendar</a>
                </div>
              </div>
            );
          })}
        </div>

        <p className="ig-footnote">
          Keep these links private: anyone with a link can see that calendar. If one is shared by mistake, use <b>Reset links</b>.
        </p>
      </section>
    </>
  );
}

// Colour tokens. Light mode reuses the app's variables when they exist (fallbacks
// otherwise); dark mode has its own palette so it never depends on the app flipping
// its variables. color-mix() keeps the provider badges readable in both.
const STYLES = `
  .ig-page {
    --ig-surface: var(--surface, #ffffff);
    --ig-surface-2: var(--surface-2, #f7f8fa);
    --ig-border: var(--border, #e6e8ec);
    --ig-border-soft: var(--border-soft, #f1f2f5);
    --ig-field-border: var(--field-border, #d7dae0);
    --ig-text: var(--text, #14171c);
    --ig-text-2: var(--text-2, #3f4651);
    --ig-text-muted: var(--text-muted, #6b7280);
    --ig-accent: var(--accent, #3b5bdb);
    --ig-tint: var(--tint, color-mix(in srgb, var(--ig-accent) 12%, var(--ig-surface)));
    --ig-ok-bg: var(--ok-bg, #ecf8f0);
    --ig-ok-border: var(--ok-border, #bfe5cc);
    --ig-ok-text: var(--ok-text, #1d7a3e);
    --ig-bad-bg: var(--bad-bg, #fdf0f0);
    --ig-bad-border: var(--bad-border, #f3c7c7);
    --ig-bad-text: var(--bad-text, #b42318);
    --ig-warn-bg: var(--warn-bg, #fff4e0);
    --ig-warn-border: var(--warn-border, #f5d199);
    --ig-warn-text: var(--warn-text, #a15c00);
    --ig-warn-bg-soft: var(--warn-bg-soft, #fffaf0);
    --ig-hover: color-mix(in srgb, var(--ig-text) 6%, transparent);
    color-scheme: light;
  }
  .ig-page[data-ig-theme="dark"] {
    --ig-surface: #16191f;
    --ig-surface-2: #1c2028;
    --ig-border: #2a2f39;
    --ig-border-soft: #232832;
    --ig-field-border: #363c48;
    --ig-text: #f3f4f6;
    --ig-text-2: #c9ced6;
    --ig-text-muted: #8d94a0;
    --ig-accent: color-mix(in srgb, var(--accent, #3b5bdb) 70%, #ffffff);
    --ig-tint: color-mix(in srgb, var(--ig-accent) 18%, var(--ig-surface));
    --ig-ok-bg: #12291b;
    --ig-ok-border: #1f5133;
    --ig-ok-text: #6fd391;
    --ig-bad-bg: #2d1416;
    --ig-bad-border: #5c2328;
    --ig-bad-text: #ff8a80;
    --ig-warn-bg: #2e2210;
    --ig-warn-border: #5e4317;
    --ig-warn-text: #f5b955;
    --ig-warn-bg-soft: #1f1a10;
    color-scheme: dark;
    color: var(--ig-text);
  }
  .ig-page { display: flex; flex-direction: column; gap: 18px; max-width: 980px; }
  .ig-loading { display: flex; align-items: center; gap: 10px; padding: 48px 0; color: var(--ig-text-muted); font-size: 14px; }

  .ig-head { display: flex; align-items: flex-start; gap: 14px; }
  .ig-head-icon { width: 42px; height: 42px; border-radius: 12px; background: var(--ig-tint); color: var(--ig-accent); display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .ig-head h1 { margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.01em; color: var(--ig-text); }
  .ig-head p { margin: 4px 0 0; font-size: 13.5px; color: var(--ig-text-2); line-height: 1.5; max-width: 680px; }

  .ig-notice { display: flex; align-items: center; gap: 10px; padding: 11px 14px; border-radius: 12px; font-size: 13px; line-height: 1.45; }
  .ig-notice svg { flex-shrink: 0; }
  .ig-notice-ok   { background: var(--ig-ok-bg);  border: 1px solid var(--ig-ok-border);  color: var(--ig-ok-text); }
  .ig-notice-bad  { background: var(--ig-bad-bg); border: 1px solid var(--ig-bad-border); color: var(--ig-bad-text); }
  .ig-notice-info { background: var(--ig-surface-2); border: 1px solid var(--ig-border); color: var(--ig-text-2); }
  .ig-x { margin-left: auto; width: 26px; height: 26px; display: inline-flex; align-items: center; justify-content: center; border: none; border-radius: 7px; background: transparent; color: inherit; cursor: pointer; opacity: 0.7; }
  .ig-x:hover { opacity: 1; background: color-mix(in srgb, currentColor 12%, transparent); }
  .ig-link { margin-left: auto; border: none; background: none; color: inherit; font-weight: 600; cursor: pointer; text-decoration: underline; }

  .ig-section { background: var(--ig-surface); border: 1px solid var(--ig-border); border-radius: 18px; padding: 20px 22px 22px; }
  .ig-section-head { margin-bottom: 16px; }
  .ig-section-head-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
  .ig-section-head h2 { display: flex; align-items: center; gap: 8px; margin: 0; font-size: 15.5px; font-weight: 700; color: var(--ig-text); }
  .ig-section-head h2 svg { color: var(--ig-text-muted); }
  .ig-section-head p { margin: 4px 0 0; font-size: 13px; color: var(--ig-text-muted); line-height: 1.5; max-width: 640px; }

  .ig-providers { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
  @media (max-width: 760px) { .ig-providers { grid-template-columns: minmax(0, 1fr); } }
  .ig-card { min-width: 0; display: flex; flex-direction: column; gap: 12px; padding: 16px; border: 1px solid var(--ig-border); border-radius: 14px; background: var(--ig-surface); }
  .ig-card[data-state="on"] { border-color: var(--ig-ok-border); }
  .ig-card[data-state="warn"] { border-color: var(--ig-warn-border); background: var(--ig-warn-bg-soft); }
  .ig-card-top { display: flex; align-items: center; gap: 12px; }
  .ig-badge {
    width: 40px; height: 40px; border-radius: 11px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
    background: color-mix(in srgb, var(--brand) 14%, var(--ig-surface));
    color: color-mix(in srgb, var(--brand) 72%, var(--ig-text));
    border: 1px solid color-mix(in srgb, var(--brand) 22%, transparent);
  }
  .ig-card-title { margin: 0; font-size: 14px; font-weight: 700; color: var(--ig-text); }
  .ig-card-sub { margin: 2px 0 0; font-size: 12.5px; color: var(--ig-text-muted); overflow-wrap: anywhere; }
  .ig-card-sub b { color: var(--ig-text-2); font-weight: 600; }
  .ig-card-body { display: flex; flex-direction: column; gap: 10px; }
  .ig-card-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: auto; }
  .ig-meta { margin: 0; font-size: 12.5px; color: var(--ig-text-muted); }
  .ig-warn-text { display: inline-flex; align-items: center; gap: 6px; margin: 0; font-size: 12.5px; color: var(--ig-warn-text); }

  .ig-pill { display: inline-flex; align-items: center; gap: 6px; height: 22px; padding: 0 9px; border-radius: 11px; font-size: 11.5px; font-weight: 600; white-space: nowrap; flex-shrink: 0; }
  .ig-pill-ok { background: var(--ig-ok-bg); color: var(--ig-ok-text); }
  .ig-pill-warn { background: var(--ig-warn-bg); color: var(--ig-warn-text); }
  .ig-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

  .ig-btn { height: 38px; display: inline-flex; align-items: center; gap: 7px; }
  .ig-btn-ghost { height: 34px; padding: 0 12px; display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--ig-border); border-radius: 9px; background: var(--ig-surface); color: var(--ig-text-2); font-size: 12.5px; font-weight: 600; cursor: pointer; white-space: nowrap; transition: background 0.15s, border-color 0.15s, color 0.15s; }
  .ig-btn-ghost:hover:not(:disabled) { background: var(--ig-border-soft); border-color: var(--ig-field-border); }
  .ig-btn-ghost:disabled { opacity: 0.6; cursor: wait; }
  .ig-btn-ghost:focus-visible { outline: 2px solid var(--ig-accent); outline-offset: 2px; }
  .ig-btn-danger:hover:not(:disabled) { background: var(--ig-bad-bg); border-color: var(--ig-bad-border); color: var(--ig-bad-text); }

  .ig-switch-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; border-radius: 10px; background: var(--ig-surface-2); border: 1px solid var(--ig-border); cursor: pointer; }
  .ig-switch-label { display: block; font-size: 13px; font-weight: 600; color: var(--ig-text); }
  .ig-switch-hint { display: block; font-size: 11.5px; color: var(--ig-text-muted); margin-top: 1px; }
  .ig-switch { position: relative; width: 36px; height: 20px; flex-shrink: 0; }
  .ig-switch input { position: absolute; inset: 0; opacity: 0; margin: 0; cursor: pointer; z-index: 1; }
  .ig-switch span { position: absolute; inset: 0; border-radius: 10px; background: color-mix(in srgb, var(--ig-text) 22%, var(--ig-surface)); transition: background 0.15s; }
  .ig-switch span::after { content: ''; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.3); transition: transform 0.15s; }
  .ig-switch input:checked + span { background: var(--ig-accent); }
  .ig-switch input:checked + span::after { transform: translateX(16px); }
  .ig-switch input:disabled + span { opacity: 0.6; }
  .ig-switch input:focus-visible + span { outline: 2px solid var(--ig-accent); outline-offset: 2px; }

  .ig-feeds { display: flex; flex-direction: column; gap: 10px; }
  .ig-feed { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr); grid-template-areas: "main url" "main open"; gap: 8px 18px; align-items: center; padding: 14px 16px; border: 1px solid var(--ig-border); border-radius: 14px; background: var(--ig-surface); }
  @media (max-width: 760px) {
    .ig-feed { grid-template-columns: minmax(0, 1fr); grid-template-areas: "main" "url" "open"; }
    .ig-section { padding: 16px; }
  }
  .ig-feed-main { grid-area: main; display: flex; align-items: flex-start; gap: 12px; }
  .ig-feed-icon { width: 32px; height: 32px; border-radius: 9px; background: var(--ig-border-soft); color: var(--ig-text-2); display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .ig-url { grid-area: url; display: flex; align-items: center; gap: 0; border: 1px solid var(--ig-field-border); border-radius: 10px; overflow: hidden; background: var(--ig-surface-2); }
  .ig-url:focus-within { border-color: var(--ig-accent); }
  .ig-url input { flex: 1; min-width: 0; height: 36px; padding: 0 10px; border: none; background: transparent; color: var(--ig-text-2); font-size: 12px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; outline: none; }
  .ig-url input::selection { background: color-mix(in srgb, var(--ig-accent) 30%, transparent); color: var(--ig-text); }
  .ig-url button { height: 36px; padding: 0 12px; display: inline-flex; align-items: center; gap: 6px; border: none; border-left: 1px solid var(--ig-field-border); background: var(--ig-surface); color: var(--ig-accent); font-size: 12.5px; font-weight: 600; cursor: pointer; white-space: nowrap; }
  .ig-url button:hover { background: var(--ig-border-soft); }
  .ig-url button.is-copied { color: var(--ig-ok-text); }
  .ig-open { grid-area: open; display: flex; align-items: center; flex-wrap: wrap; gap: 4px 12px; font-size: 12.5px; color: var(--ig-text-muted); }
  .ig-open a { display: inline-flex; align-items: center; gap: 3px; color: var(--ig-accent); font-weight: 600; text-decoration: none; }
  .ig-open a:hover { text-decoration: underline; }
  .ig-open a:focus-visible { outline: 2px solid var(--ig-accent); outline-offset: 2px; border-radius: 3px; }
  .ig-footnote { margin: 14px 2px 0; font-size: 12px; color: var(--ig-text-muted); }
`;